import React, { useRef, useState } from 'react';
import { Button, Col, FormGroup, Row } from 'reactstrap';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import globalTranslations from '../../../../i18n/globalTranslations';
import { allowEmpty, notBlank } from '../../../../util/formValidators';
import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import CountryStore from '../../../../store/CountryStore';
import FormGroupField from '../../../form/FormGroupField';
import SelectField from '../../../form/SelectField';
import Switch from '../../../common/Switch';
import Icon from '../../../common/Icon';
import FormValidator from '../../../../services/FormValidator';

const formLabels = {
  company: <FormattedMessage {...globalTranslations.companyTitle} />,
  firstname: <FormattedMessage {...globalTranslations.firstNameTitle} />,
  lastname: <FormattedMessage {...globalTranslations.lastNameTitle} />,
  contactPersonFirstname: (
    <FormattedMessage {...globalTranslations.contactPersonFirstNameTitle} />
  ),
  contactPersonLastname: (
    <FormattedMessage {...globalTranslations.contactPersonLastNameTitle} />
  ),
  streetAddress: (
    <FormattedMessage {...globalTranslations.streetAddressTitle} />
  ),
  postalCode: <FormattedMessage {...globalTranslations.zipCodeTitle} />,
  city: <FormattedMessage {...globalTranslations.cityTitle} />,
  telephone: <FormattedMessage {...globalTranslations.telephoneTitle} />,
  countryId: <FormattedMessage {...globalTranslations.countryTitle} />,
};

const createOption = (value, label) => ({ value, label });

const getFirstError = (errors, key) => {
  if (!errors || typeof errors !== 'object') return undefined;
  const list =
    errors[key] ?? errors[key.replace(/([A-Z])/g, '_$1').toLowerCase()];
  return Array.isArray(list) && list.length > 0 ? list[0] : undefined;
};

const AddressBookForm = ({
  accountStore,
  countryStore,
  formValidator,
  onSave,
  onCancel,
  loading,
  serverErrors = {},
}) => {
  const isRetailer = accountStore?.isRetailer === true;
  const [saveToAddressBook, setSaveToAddressBook] = useState(true);
  const formRef = useRef(null);
  if (!formRef.current) {
    // Retailer: always company mode (no toggle). Non-retailer: toggle controls company vs name.
    const isCompanyField = createFormField({ value: isRetailer });
    formRef.current = createForm({
      isCompany: isCompanyField,
      company: createFormField(
        { value: '' },
        isRetailer ? [notBlank(formLabels.company)] : []
      ),
      firstname: createFormField(
        { value: '' },
        isRetailer
          ? formValidator.firstName(formLabels.firstname).map(allowEmpty)
          : []
      ),
      lastname: createFormField(
        { value: '' },
        isRetailer
          ? formValidator.lastName(formLabels.lastname).map(allowEmpty)
          : []
      ),
      streetAddress: createFormField(
        { value: '' },
        [notBlank(formLabels.streetAddress)].concat(
          formValidator.streetAddress(formLabels.streetAddress)
        )
      ),
      postalCode: createFormField(
        { value: '' },
        [notBlank(formLabels.postalCode)].concat(
          formValidator.postalCode(formLabels.postalCode)
        )
      ),
      city: createFormField(
        { value: '' },
        [notBlank(formLabels.city)].concat(formValidator.city(formLabels.city))
      ),
      countryId: createFormField({
        value:
          accountStore?.account?.country_id ?? countryStore?.countries?.[0]?.id,
      }),
      telephone: createFormField({ value: '' }, []),
    });
    const form = formRef.current;
    // Non-retailer: company required when isCompany; firstname+lastname required when !isCompany (either company or name)
    if (!isRetailer) {
      form.getField('company').setValidators([
        (value) => {
          const isCompany = form.getField('isCompany').value;
          if (!isCompany) return null;
          return notBlank(formLabels.company)(value);
        },
      ]);
      form.getField('firstname').setValidators([
        (value) => {
          const isCompany = form.getField('isCompany').value;
          if (isCompany) return null;
          const err = notBlank(formLabels.firstname)(value);
          if (err) return err;
          const v = formValidator.firstName(formLabels.firstname);
          for (let i = 0; i < v.length; i++) {
            const e = v[i](value);
            if (e) return e;
          }
          return null;
        },
      ]);
      form.getField('lastname').setValidators([
        (value) => {
          const isCompany = form.getField('isCompany').value;
          if (isCompany) return null;
          const err = notBlank(formLabels.lastname)(value);
          if (err) return err;
          const v = formValidator.lastName(formLabels.lastname);
          for (let i = 0; i < v.length; i++) {
            const e = v[i](value);
            if (e) return e;
          }
          return null;
        },
      ]);
    }
  }
  const form = formRef.current;

  const isCompany = form.getField('isCompany')?.value === true;

  const handleSaveClick = () => {
    form.validate();
    if (form.valid) {
      const values = form.getFieldValues();
      const payload = {
        company: values.company || '',
        firstname: values.firstname || '',
        lastname: values.lastname || '',
        streetAddress: values.streetAddress || '',
        postalCode: values.postalCode || '',
        city: values.city || '',
        countryId:
          values.countryId != null ? Number(values.countryId) : undefined,
        telephone: values.telephone || '',
        saveToAddressBook,
      };
      onSave(payload);
    }
  };

  const countryOptions =
    countryStore?.countries?.map((c) => createOption(String(c.id), c.name)) ??
    [];

  return (
    <div className="AddressBookForm">
      <p className="AddressBookForm__mandatory-note">
        <FormattedMessage {...globalTranslations.mandatoryFieldNote} />
      </p>

      {!isRetailer && (
        <FormGroup className="AddressBookForm__toggle-row">
          <Switch
            checked={isCompany}
            onChange={(e) =>
              form.getField('isCompany').setValue(e.target.checked)
            }
            size="xs"
          >
            <FormattedMessage {...globalTranslations.recipientIsCompanyLabel} />
          </Switch>
        </FormGroup>
      )}

      {isRetailer ? (
        <>
          <FormGroupField
            field={form.getField('company')}
            formName="AddressBookForm"
            floatingLabel
            fieldName="company"
            label={formLabels.company}
            required
            customError={getFirstError(serverErrors, 'company')}
          />
          <Row>
            <Col md={6}>
              <FormGroupField
                field={form.getField('firstname')}
                formName="AddressBookForm"
                floatingLabel
                fieldName="firstname"
                label={formLabels.contactPersonFirstname}
                customError={getFirstError(serverErrors, 'firstname')}
              />
            </Col>
            <Col md={6}>
              <FormGroupField
                field={form.getField('lastname')}
                formName="AddressBookForm"
                floatingLabel
                fieldName="lastname"
                label={formLabels.contactPersonLastname}
                customError={getFirstError(serverErrors, 'lastname')}
              />
            </Col>
          </Row>
        </>
      ) : (
        <>
          {!isCompany && (
            <Row>
              <Col md={6}>
                <FormGroupField
                  field={form.getField('firstname')}
                  formName="AddressBookForm"
                  floatingLabel
                  fieldName="firstname"
                  label={formLabels.firstname}
                  required
                  customError={getFirstError(serverErrors, 'firstname')}
                />
              </Col>
              <Col md={6}>
                <FormGroupField
                  field={form.getField('lastname')}
                  formName="AddressBookForm"
                  floatingLabel
                  fieldName="lastname"
                  label={formLabels.lastname}
                  required
                  customError={getFirstError(serverErrors, 'lastname')}
                />
              </Col>
            </Row>
          )}
          {isCompany && (
            <>
              <FormGroupField
                field={form.getField('company')}
                formName="AddressBookForm"
                floatingLabel
                fieldName="company"
                label={formLabels.company}
                required
                customError={getFirstError(serverErrors, 'company')}
              />
              <Row>
                <Col md={6}>
                  <FormGroupField
                    field={form.getField('firstname')}
                    formName="AddressBookForm"
                    floatingLabel
                    fieldName="firstname"
                    label={formLabels.contactPersonFirstname}
                    customError={getFirstError(serverErrors, 'firstname')}
                  />
                </Col>
                <Col md={6}>
                  <FormGroupField
                    field={form.getField('lastname')}
                    formName="AddressBookForm"
                    floatingLabel
                    fieldName="lastname"
                    label={formLabels.contactPersonLastname}
                    customError={getFirstError(serverErrors, 'lastname')}
                  />
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      <FormGroupField
        field={form.getField('streetAddress')}
        formName="AddressBookForm"
        floatingLabel
        fieldName="streetAddress"
        label={formLabels.streetAddress}
        required
        customError={getFirstError(serverErrors, 'streetAddress')}
      />

      <Row>
        <Col md={6}>
          <FormGroupField
            field={form.getField('postalCode')}
            formName="AddressBookForm"
            floatingLabel
            fieldName="postalCode"
            label={formLabels.postalCode}
            required
            customError={getFirstError(serverErrors, 'postalCode')}
          />
        </Col>
        <Col md={6}>
          <FormGroupField
            field={form.getField('city')}
            formName="AddressBookForm"
            floatingLabel
            fieldName="city"
            label={formLabels.city}
            required
            customError={getFirstError(serverErrors, 'city')}
          />
        </Col>
      </Row>

      {countryOptions.length > 1 && (
        <SelectField
          field={form.getField('countryId')}
          formName="AddressBookForm"
          floatingLabel
          fieldName="countryId"
          label={formLabels.countryId}
          options={countryOptions}
          customError={getFirstError(serverErrors, 'countryId')}
        />
      )}

      <FormGroupField
        field={form.getField('telephone')}
        formName="AddressBookForm"
        floatingLabel
        fieldName="telephone"
        label={formLabels.telephone}
        type="tel"
        customError={getFirstError(serverErrors, 'telephone')}
      />

      <FormGroup className="AddressBookForm__toggle-row AddressBookForm__save-later-row">
        <Switch
          checked={saveToAddressBook}
          onChange={(e) => setSaveToAddressBook(!!e.target.checked)}
          size="xs"
        >
          <FormattedMessage {...globalTranslations.saveAddressForLaterUse} />
        </Switch>
      </FormGroup>

      <div className="AddressBookForm__actions">
        <Button type="button" color="secondary" onClick={onCancel}>
          <FormattedMessage {...globalTranslations.cancelTitle} />
        </Button>
        <Button
          type="button"
          color="primary"
          disabled={form.valid === false || loading}
          onClick={handleSaveClick}
        >
          {loading ? (
            <Icon name="circle-o-notch" spin />
          ) : (
            <FormattedMessage {...globalTranslations.saveTitle} />
          )}
        </Button>
      </div>
    </div>
  );
};

AddressBookForm.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  formValidator: PropTypes.instanceOf(FormValidator).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  serverErrors: PropTypes.object,
};

export default inject(
  'accountStore',
  'countryStore',
  'formValidator'
)(observer(AddressBookForm));
